import { Editor, Element, Node, NodeEntry, Path, Transforms, Range } from 'slate'
import { ReactEditor } from 'slate-react'
import { ElementWithType } from '../../../../../../utils/types'
import { generateId } from '../../../utils/hooks/withId'
import { isInlineElement, isSelectionCollapsed } from '../../../utils/queries'
import { isEqual, cloneDeep } from 'lodash'

import { jsx } from 'slate-hyperscript'
import { wrapChildren } from '../../utils/wrapChildren'

export const LIST_TYPES = ['ol', 'ul'] as const

export const listWrapperElement = (format) => {
  return { type: format, children: [], uid: generateId() }
}

export const getRootListPath = (editor, path): any => {
  const rootList = Editor.above(editor, {
    at: path,
    match(n, p) {
      return (
        LIST_TYPES.includes(n?.["type"]) &&
        !LIST_TYPES.includes(Editor.parent(editor, p)?.[0]?.["type"]) &&
        Editor.previous(editor, { at: p })?.[0]?.["type"] !== 'li'
      );

    },
  })

  return rootList ? rootList[1] : null
}

export const splitSelectedList = (editor: Editor, path: Path, rootListPath: Path) => {
  let currentPath = [...path];
  Editor.withoutNormalizing(editor, () => {
    currentPath.slice(rootListPath.length).forEach((_, i) => {
      Transforms.splitNodes(editor, {
        at: currentPath
      })
      currentPath.pop()
      currentPath[currentPath.length - 1] += 1
    })
  });
}

export const toggleList = (event, editor, format) => {
  event?.preventDefault()
  const selection = editor.selection || editor.savedSeleciton
  if (selection) {
    // incase selection is from savedSelection
    Transforms.select(editor, selection)
    
    for (const [node, path] of Editor.nodes(editor, {
      at: selection,
      match: (n: ElementWithType) => Element.isElement(n) && n.attrs?.['data-indent-level'] !== undefined,
    })) {
      const element = node as ElementWithType
      const newAttrs = { ...element.attrs }
      delete newAttrs['data-indent-level']
      
      Transforms.setNodes(editor, { attrs: newAttrs } as Partial<ElementWithType>, { at: path })
    }
    
    const [currentElement, currentElementPath] = Editor.parent(editor, selection) as any


    if (isSelectionCollapsed(selection)) {

      // if it already a list
      if (currentElement.type === 'li') {
        const listType = Editor.parent(editor, currentElementPath) as any

        // different list then convert
        if (listType?.[0]?.type !== format) {
          Transforms.setNodes(editor, { type: format } as ElementWithType, { at: listType[1] })
        }
        else {
          convertListItemsToParagraph(editor)
        }
        return
      }

      // if prevSibling is also a same list
      if (currentElementPath[currentElementPath.length - 1] > 0) {
        const prevSiblingPath = currentElementPath.slice()
        prevSiblingPath[prevSiblingPath.length - 1] -= 1

        const [prevSibling] = Editor.node(editor, prevSiblingPath) as any
        if (prevSibling.type === format) {
          Transforms.setNodes(editor, {
            type: 'li'
          } as ElementWithType)

          //@ts-ignore
          const movePosition = [...prevSiblingPath, prevSibling.children.length]
          Transforms.moveNodes(editor, { to: movePosition })
          return
        }
      }
    } else {
      let fixed = false

      //already a list
      for (const [, listItemPath] of Editor.nodes(editor, {
        match: (n: ElementWithType, p) => { return n.type === 'li' && Editor.parent(editor, p)?.[0]?.["type"] !== format },
      })) {
        fixed = true
        const [listType, listTypePath] = Editor.parent(editor, listItemPath) as any
        if (listType.type !== format) {
          Transforms.setNodes(editor, { type: format } as ElementWithType, { at: listTypePath })
        }
      }

      const [isList] = Editor.nodes(editor, {
        match: (n: ElementWithType) => LIST_TYPES.includes(n.type as any),
      })

      if (!fixed && isList) {
        convertListItemsToParagraph(editor)
        fixed = true
      }

      if (fixed) return
    }

    // create a new list
    editor.stopNormalizeNode = true
    Transforms.setNodes(editor, {
      type: 'li'
    } as ElementWithType)
    const block = listWrapperElement(format)
    editor.stopNormalizeNode = false
    Transforms.wrapNodes(editor, block)
  }
}

export const getPreviousListItemEntry = (editor: Editor, liPath: Path) => {
  const previousPath = Path.previous(liPath)

  const previousItem = Node.get(editor, previousPath)
  if (previousItem['type'] === 'li') return [previousItem, previousPath] as NodeEntry<Element>

  const [previousLiEntry] = Editor.nodes<Element>(editor, {
    at: previousPath,
    match: (node, path) => {
      if (node['type'] !== 'li' || !Element.isElement(node)) return

      if (Array.isArray(node.children) && node.children.find((child) => LIST_TYPES.includes(child?.['type']))) return
      const [parent, parentPath] = Editor.parent(editor, path)
      const lastPath = [...parentPath, parent.children.length - 1]
      const isLast = isEqual(lastPath, path)
      return isLast && Path.isBefore(path, liPath)
    }
  })

  return previousLiEntry
}

export const handleTableInList = (editor: Editor, liPath: Path) => {
  const previousLiEntry = getPreviousListItemEntry(editor, liPath)
  if (!previousLiEntry) return false

  const [previousLi, previousLiPath] = previousLiEntry
  const liRef = Editor.pathRef(editor, liPath)

  const previousLiRef = Editor.pathRef(editor, previousLiPath)

  // we check for last element here because if one element is inline all elements should be 
  // and thus wrapped in p tag
  if (isInlineElement(previousLi.children.at(-1))) {
    wrapChildren(editor, previousLiEntry, jsx('element', { type: 'p' }))
  }

  const updatedPreviousLi = Node.get(editor, previousLiRef.current)

  if (!Element.isElement(updatedPreviousLi)) return

  const pathToMove = Path.next(previousLiRef.current)
  Transforms.moveNodes(editor, {
    at: liPath,
    to: pathToMove
  })
  Transforms.select(editor, pathToMove)
  Transforms.collapse(editor, { edge: 'start' })
  Transforms.mergeNodes(editor, { at: pathToMove, match: (node) => node?.['type'] === 'li' })

  liRef.unref()
  previousLiRef.unref()
  return true
}

export const removeListMarker = (editor: Editor, liEntry: NodeEntry<Element>) => {
  const [li] = liEntry
  Editor.withoutNormalizing(editor, () => {
    Transforms.unwrapNodes(editor, {
      match: (n: any) => LIST_TYPES.includes(n.type),
      split: true
    })
    if (isInlineElement(li.children[0])) {
      Transforms.setNodes(editor, { type: 'p' } as Partial<Element>)
    } else {
      Transforms.unwrapNodes(editor, {
        match: (n: any) => n.type === 'li'
      })
    }
  })
}

export const updateLiWithNewNodeChildren = (editor, child) => {
  let childPath = []
  try {
    childPath = ReactEditor.findPath(editor, child)
  }
  catch (err) {
    if (err instanceof Error) {
      err.message = 'Error while finding path in updateLiWithNewNodeChildren ' + err.message
      console.error(err, JSON.stringify({ selection: editor.selection, children: editor.children }))
    }
  }
  if (childPath.length && Path.hasPrevious(childPath)) {
    const previousLiPath = Path.previous(childPath)
    const previousLiElement: any = Node.get(editor, previousLiPath)
    let newChildren = [];
    Array.from(previousLiElement.children).forEach((child, i) => {
      if (isInlineElement(child)) {
        if (newChildren?.at(-1)?.type === 'fragment') {
          newChildren.at(-1).children.push(typeof child === 'string' ? jsx('text', {}, child) : child)
        } else {
          newChildren.push(jsx('element', { type: 'fragment' }, child))
        }
        return
      }
      newChildren.push(child)
    })
    // form new updated node 
    let newNode: any = cloneDeep(previousLiElement)
    newNode.children = [...newChildren, child]
    //remove ol/ul which is a sibling to li element
    Transforms.removeNodes(editor, { at: childPath })
    //remove the li element
    Transforms.removeNodes(editor, { at: previousLiPath })
    //insert a new node li with the modified children
    Transforms.insertNodes(editor, newNode, { at: previousLiPath })
  }
}

export const convertListItemsToParagraph = (editor) => {

  const [startPath, endPath] = Range.edges(editor.selection as Range).map((point) => Editor.above(editor, { at: point.path, match: (node) => node['type'] === 'li' })?.[1])
  const nextNodeOfEndPath = Editor.next(editor, {
    at: endPath,
    match(node, path) {
      return node['type'] === 'li'
    },
  }
  )
  const rootListPath = getRootListPath(editor, endPath) || []
  const nextListAtRootPath = Path.next(rootListPath)

  const isEntireListSelected = Range.equals(Editor.range(editor, rootListPath), editor.selection)
  const isFirstLiSelected = Path.equals(startPath, rootListPath.concat([0]))

  const shouldSplitAtStart = !isFirstLiSelected && !isEntireListSelected
  const shouldSplitAtEnd = nextNodeOfEndPath && !isEntireListSelected

  shouldSplitAtEnd && splitSelectedList(editor, nextNodeOfEndPath[1], rootListPath);
  shouldSplitAtStart && splitSelectedList(editor, startPath, nextListAtRootPath);


  shouldSplitAtEnd &&
    Transforms.unwrapNodes(editor, {
      at: shouldSplitAtStart ? Path.next(Path.next(rootListPath)) : Path.next(rootListPath),
      match: (n, p) => {

        if (LIST_TYPES.includes(n?.["type"]) && Editor.parent(editor, p)?.[0]?.["type"] === n?.["type"]) {
          if (p.at(-1) === 0 || Editor.previous(editor, { at: p })?.[0]?.['type'] !== 'li') {
            return true
          }
        }
        return false
      },
      mode: "all",
      split: true
    })


  Editor.withoutNormalizing(editor, () => {

    let path;
    if (!shouldSplitAtStart && !shouldSplitAtEnd) { path = rootListPath }
    if (!shouldSplitAtStart && shouldSplitAtEnd) { path = rootListPath }
    if (shouldSplitAtStart && !shouldSplitAtEnd) { path = Path.next(rootListPath) }
    if (shouldSplitAtStart && shouldSplitAtEnd) { path = Path.next(rootListPath) }

    Transforms.unwrapNodes(editor, {
      at: path,
      match: (n, p) => {
        if (LIST_TYPES.includes(n?.["type"])) {
          return true
        }
        return false
      },
      mode: "all",

    })

    Transforms.setNodes(editor, { type: "p" } as any, {
      at: path,
      match: (n, p) => {
        if (n?.["type"] === 'li') {
          return true
        }
        return false
      }
    });


  })
}

export const isListActive = (editor: Editor, format: typeof LIST_TYPES[number]) => {
  const { selection } = editor
  if (!selection) return false

  const anchorList = Editor.above(editor, {
    match: n => LIST_TYPES.includes(n?.["type"]),
    at: editor.selection?.anchor,
    mode: 'lowest'
  })

  const focusList = Editor.above(editor, {
    match: n => LIST_TYPES.includes(n?.["type"]),
    at: editor.selection?.focus,
    mode: 'lowest'
  })

  return ( focusList && anchorList && focusList[0]?.["type"] === format && anchorList[0]?.["type"] === format && isEqual(focusList[1], anchorList[1]) )
}